import type { ForwardedRef, InputHTMLAttributes } from "react";
import { forwardRef, useCallback, useEffect, useRef } from "react";
import { useEffectEvent } from "@/shared/hooks/useEffectEvent";
import { useUIComponentLoaded } from "@/shared/hooks/useUIComponentLoaded";

export interface AutocompleteProps<TItem = unknown> extends InputHTMLAttributes<HTMLInputElement> {
    /** Endpoint the vanilla autocomplete queries. */
    source: string;
    /** Response format handed to `window.ui.autocomplete`. Defaults to `"jsonp"`. */
    dataType?: string;
    /** Key on each result used as its display value. */
    searchKey: string;
    /** Query-string parameter that carries the typed prefix. */
    queryParameter: string;
    /** Fired when the user picks a suggestion. */
    onItemSelect?: (payload: { item: TItem }) => void;
    /** Fired when the source responds with suggestions. */
    onResponse?: (payload: { results: TItem[] }) => void;
    /** Fired when the suggestion list closes. */
    onClose?: (payload: { element: HTMLInputElement }) => void;
}

function AutocompleteInner<TItem>(
    {
        source,
        dataType = "jsonp",
        searchKey,
        queryParameter,
        onItemSelect,
        onResponse,
        onClose,
        ...inputProps
    }: AutocompleteProps<TItem>,
    forwardedRef: ForwardedRef<HTMLInputElement>,
): JSX.Element {
    const inputRef = useRef<HTMLInputElement | null>(null);

    // Hydration-safe readiness for window.ui.autocomplete (populated by core.js). On
    // CMS pages core.js is deferred, so this is false until it loads.
    const autocompleteReady = useUIComponentLoaded("autocomplete");

    // Wrap the callbacks as effect events so the instance attaches once yet always
    // invokes the latest props — the guarded arrows keep them defined when a caller
    // omits a handler (useEffectEvent throws if it ever calls an undefined callback).
    const handleItemSelect = useEffectEvent((payload: { item: TItem }) => onItemSelect?.(payload));
    const handleResponse = useEffectEvent((payload: { results: TItem[] }) => onResponse?.(payload));
    const handleClose = useEffectEvent((payload: { element: HTMLInputElement }) => onClose?.(payload));

    const setRef = useCallback(
        (el: HTMLInputElement | null): void => {
            inputRef.current = el;
            if (typeof forwardedRef === "function") forwardedRef(el);
            else if (forwardedRef) forwardedRef.current = el;
        },
        [forwardedRef],
    );

    // Own-the-node (ADR-012 §3): the <input> below renders and is fully usable
    // regardless of window.ui. Once core.js is ready we attach the vanilla autocomplete
    // to that same node — window.ui is an enhancement attached in an effect, never a
    // render-time dependency, so there is no hydration branch, no layout shift, no node
    // swap and no focus/caret loss. Re-attach only when readiness or the source config
    // changes; callbacks are stable effect events.
    useEffect(() => {
        const inputEl = inputRef.current;
        if (!autocompleteReady || !inputEl) return undefined;

        const instance = window.ui.autocomplete(inputEl, {
            dataType,
            source,
            searchKey,
            queryParameter,
            onItemSelect: handleItemSelect,
            onResponse: handleResponse,
            onClose: handleClose,
        });

        return () => instance.destroy();
    }, [autocompleteReady, source, dataType, searchKey, queryParameter, handleItemSelect, handleResponse, handleClose]);

    return <input ref={setRef} {...inputProps} />;
}

const ForwardedAutocomplete = forwardRef(AutocompleteInner);
ForwardedAutocomplete.displayName = "Autocomplete";

/**
 * Own-the-node boundary component for Ancestry's vanilla autocomplete
 * (`window.ui.autocomplete`, populated by `core.js`) — ADR-012 §3.
 *
 * Unlike the gate-the-mount wrappers (`Callout`, `Modal`, …), an autocomplete paints a
 * visible control on first render, so gating its mount would drop the input or shift
 * layout. Instead this component renders and owns a real `<input>` unconditionally and
 * attaches the vanilla behavior in an effect once `window.ui.autocomplete` exists. The
 * input is a fully usable text field before then.
 *
 * Generic over the suggestion item type so callers keep type-safe `onItemSelect` /
 * `onResponse` payloads; the boundary itself stays domain-agnostic (it lives in the
 * `shared` layer and must not depend on entities).
 */
export const Autocomplete = ForwardedAutocomplete as <TItem = unknown>(
    props: AutocompleteProps<TItem> & { ref?: ForwardedRef<HTMLInputElement> },
) => JSX.Element;
